import { ExternalLink } from "lucide-react";

import { Button } from "@/components/ui/button";

interface HowThisWorksProps {
  /**
   * Anchor within the operator guide, so the link lands on the section for what the operator is
   * actually looking at rather than at the top of a page covering all of it.
   */
  anchor:
    | "drift"
    | "no-cycle"
    | "expiry-screen"
    | "entry"
    | "steps"
    | "what-moves"
    | "refusals"
    | "after";

  /**
   * Which guide under `/docs` to open. Defaults to the billing-consistency one this component was
   * written for; the anchors above are grouped by guide.
   */
  guide?: "billing-consistency" | "vm-ownership-transfer";
}

/**
 * Link to the operator guide for the billing-consistency screens — the two reports and the expiry
 * editor they send an operator to.
 *
 * The detail lives in `/docs/billing-consistency.html` rather than on the screen, the same way the
 * VM recovery page and the retry dialog do it: what an operator needs to know — which of the two
 * dates is authoritative, what repairs itself and what does not — is a page of prose, and a page of
 * prose sitting on a worklist is a page between the operator and the rows.
 */
export function HowThisWorks({
  anchor,
  guide = "billing-consistency",
}: HowThisWorksProps) {
  return (
    <Button variant="outline" size="sm" asChild className="shrink-0">
      <a
        href={`/docs/${guide}.html#${anchor}`}
        target="_blank"
        rel="noreferrer"
      >
        How this works
        <ExternalLink className="ml-2 h-3.5 w-3.5" />
      </a>
    </Button>
  );
}
